import { useEffect, useRef } from "react";

/**
 * Leaves the match-ready surface when the accept deadline has passed and the
 * roster never locked in. The server emit is still authoritative for requeue;
 * this is the client backstop when that emit is missed (lost in-memory timer).
 */
export default function useDismissExpiredMatchAccept({
    matchAcceptDialogState,
    onExpired,
}) {
    const stateRef = useRef(matchAcceptDialogState);
    const onExpiredRef = useRef(onExpired);

    useEffect(() => {
        stateRef.current = matchAcceptDialogState;
    }, [matchAcceptDialogState]);

    useEffect(() => {
        onExpiredRef.current = onExpired;
    }, [onExpired]);

    const matchId = matchAcceptDialogState?.matchId;
    const expiresAt = matchAcceptDialogState?.expiresAt;
    const allAccepted = Boolean(matchAcceptDialogState?.allAccepted);

    useEffect(() => {
        if (!matchId || allAccepted) return undefined;
        const deadline = Number(expiresAt);
        if (!Number.isFinite(deadline)) return undefined;

        const dismiss = () => {
            const current = stateRef.current;
            if (!current?.matchId || String(current.matchId) !== String(matchId)) {
                return;
            }
            if (current.allAccepted) return;
            if (typeof onExpiredRef.current !== "function") return;
            onExpiredRef.current({ matchId: current.matchId });
        };

        const remainingMs = deadline - Date.now();
        if (remainingMs <= 0) {
            dismiss();
            return undefined;
        }

        const id = window.setTimeout(dismiss, remainingMs);
        return () => window.clearTimeout(id);
    }, [matchId, expiresAt, allAccepted]);
}
